import { useAppSelector } from "@/app/hooks";
import {
  Property,
  Survey,
  TourStop,
  StopType,
} from "@/features/survey/types";
import { getIconData } from "@/shared/utils/map";
import { EmeraldBadge } from "@emerald-react/badge";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldBadgeSize } from "@emerald/nxcore";
import dayjs from "dayjs";
import { useGetTourStopsQuery } from "@/services/survey";

// Extend interfaces for tour schedule specific properties
interface TourProperty extends Property {
  isIncludeTourSch?: boolean;
}

interface TourSurvey extends Survey {
  tourDetails?: string;
  tourScheduleHeader?: string;
  isIncludeTourAddressLine2?: boolean;
  isIncludeTourAddress2?: boolean;
  showDirectionArrow?: boolean;
  showDirectionsArrow?: boolean;
  tourNumberingMode?: "KEEP" | "RENUMBER";
}

/**
 * Interactive Survey Tour Schedule Page
 * Displays a scheduled list of properties for touring with directions links,
 * including custom stop details (location pin) and linked properties (star icon)
 */
const InteractiveSurveyTourSchedule = () => {
  const { currentSurvey, mapFilters } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const tourSurvey = currentSurvey as TourSurvey;
  if (tourSurvey?.isIncludeTourSch === false) {
    return null;
  }
  const { showShortlistOnly } = mapFilters;
  const surveyId = tourSurvey?.id || "";

  const sanitizeText = (value?: string | null): string => {
    const text = String(value ?? "").trim();
    if (!text) return "";
    if (text === "-" || text === "—") return "";
    if (text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
      return "";
    }
    return text;
  };

  const includeTourPropertyName =
    tourSurvey?.isIncludeTourPropertyName ?? true;
  const includeTourStreetAddress =
    tourSurvey?.isIncludeTourStreetAddress ?? true;
  const includeTourAddressLine2 =
    tourSurvey?.isIncludeTourAddressLine2 ??
    tourSurvey?.isIncludeTourAddress2 ??
    true;
  const includeTourCity = tourSurvey?.isIncludeTourCity ?? true;
  const includeTourState = tourSurvey?.isIncludeTourState ?? true;
  const includeTourZipCode = tourSurvey?.isIncludeTourZipCode ?? true;
  const showDirectionsArrow =
    tourSurvey?.showDirectionArrow ?? tourSurvey?.showDirectionsArrow ?? true;

  // Fetch tour stops from API
  const { data: tourStopsResponse, isError, error } = useGetTourStopsQuery(surveyId, {
    skip: !surveyId,
  });

  const normalizePropertyId = (value?: string) => String(value ?? "").toLowerCase().trim();

  const isCurrentLocationStop = (stop: TourStop) =>
    stop.stopType === StopType.CURRENT_LOCATION || stop.stopType === "CURRENT_LOCATION";

  // Properties eligible for a numbered tour slot (used by KEEP mode to preserve numbering slots).
  const orderedTourSlotProperties: TourProperty[] = (tourSurvey?.properties || [])
    .filter(
      (p: TourProperty) =>
        (p?.isHidden === undefined || !p.isHidden) &&
        !p.isCurrentLocation,
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as TourProperty[];

  // Visible properties in the schedule. If include flag is false, only property row is hidden.
  const allScheduleProperties: TourProperty[] = (tourSurvey?.properties || [])
    .filter(
      (p: TourProperty) =>
        (p?.isHidden === undefined || !p.isHidden) &&
        !p.isCurrentLocation &&
        p?.isIncludeTourSchedule !== false &&
        p?.isIncludeTourSch !== false,
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as TourProperty[];

  const visibleProperties = showShortlistOnly
    ? allScheduleProperties.filter((p) => p.isClientShortlist)
    : allScheduleProperties;

  const numberedSlotProperties = showShortlistOnly
    ? orderedTourSlotProperties.filter((p) => p.isClientShortlist)
    : orderedTourSlotProperties;

  const tourNumberingMode =
    tourSurvey?.tourNumberingMode === "KEEP" ? "KEEP" : "RENUMBER";

  const tourStops = tourStopsResponse?.stops ?? [];

  // Get ALL properties (including hidden) sorted by order for positioning logic
  const allProperties: TourProperty[] = (tourSurvey?.properties || [])
    .filter((p: TourProperty) => !p.isCurrentLocation)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as TourProperty[];

  // Determine the last property order to split independent stops
  const lastPropertyOrder = allProperties.length > 0 
    ? Math.max(...allProperties.map(p => p.order ?? 0))
    : -1;

  // Get all independent custom stops (not attached to any property)
  const independentStops = tourStops
    .filter((stop) => !stop.propertyId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Split independent stops into top and bottom groups based on order
  const independentStopsTop = independentStops.filter(
    stop => lastPropertyOrder === -1 || (stop.order ?? 0) <= lastPropertyOrder
  );

  const independentStopsBottom = independentStops.filter(
    stop => lastPropertyOrder !== -1 && (stop.order ?? 0) > lastPropertyOrder
  );

  // Type for render rows
  type TourRenderRow =
    | { kind: "property"; property: TourProperty; displayNumber?: number }
    | { kind: "stop"; stop: TourStop };

  // Build tour schedule structure:
  // 1) Independent stops at top (order <= last property order)
  // 2) For each property:
  //    - Render property card ONLY if included in tour AND not hidden
  //    - Then render attached custom stops below the property
  // 3) Independent stops at bottom (order > last property order)
  const renderRows: TourRenderRow[] = [];

  // Add independent stops at top
  independentStopsTop.forEach((stop) => {
    renderRows.push({ kind: "stop", stop });
  });

  // Badge numbering mode:
  // KEEP: preserve numbering slots from all ordered tour properties.
  // RENUMBER: compact numbering across currently visible properties.
  const propertyBadgeOrderSource =
    tourNumberingMode === "KEEP" ? numberedSlotProperties : visibleProperties;

  const propertyBadgeOrder = new Map(
    propertyBadgeOrderSource.map((property, index) => [
      normalizePropertyId(property.id || property.propertyId),
      index + 1,
    ]),
  );

  // Add property + attached custom stops for each property
  allProperties.forEach((property) => {
    const propertyId = normalizePropertyId(property.id || property.propertyId);
    const attachedStops = tourStops
      .filter((stop) => normalizePropertyId(stop.propertyId) === propertyId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Only render property card if it's included in tour schedule AND not hidden
    const shouldShowProperty = 
      property.isIncludeTourSchedule !== false &&
      property.isIncludeTourSch !== false &&
      !property.isHidden &&
      (!showShortlistOnly || property.isClientShortlist);
      
    if (shouldShowProperty) {
      renderRows.push({
        kind: "property",
        property,
        displayNumber: propertyBadgeOrder.get(propertyId),
      });
    }

    // Always render attached custom stops BELOW the property (even if property is toggled off/hidden)
    attachedStops.forEach((stop) => {
      renderRows.push({ kind: "stop", stop });
    });
  });

  // Add independent stops at bottom
  independentStopsBottom.forEach((stop) => {
    renderRows.push({ kind: "stop", stop });
  });

  const hasTourContent = renderRows.length > 0;

  // tourDetails stores section header/date text from DB; fallback to default title + formatted date
  const scheduleDate = tourSurvey?.tourDetails
    ? tourSurvey.tourDetails
    : `Tour Schedule - ${dayjs(new Date()).format("dddd MMMM D, YYYY")}`;
  const scheduleHeader = sanitizeText(tourSurvey?.tourScheduleHeader);
  const scheduleTitle = scheduleHeader || scheduleDate;

  // Helper function to build property display text based on survey flags
  const buildPropertyDisplayText = (property: TourProperty): string[] => {
    const lines: string[] = [];

    const propertyName = sanitizeText(property.name || property.propertyName);
    const address1 = sanitizeText(property.address1);
    const address2 = sanitizeText(property.address2);
    const city = sanitizeText(property.city);
    const state = sanitizeText(property.stateProvince);
    const postalCode = sanitizeText(property.postalCode);

    // Property Name
    if (includeTourPropertyName && propertyName) {
      lines.push(propertyName);
    }

    // Street Address (address1)
    if (includeTourStreetAddress && address1) {
      lines.push(address1);
    }

    // Address Line 2 (address2)
    if (includeTourAddressLine2 && address2) {
      lines.push(address2);
    }

    // City, State, Zip on one line
    const locationParts: string[] = [];
    if (includeTourCity && city) {
      locationParts.push(city);
    }
    if (includeTourState && state) {
      locationParts.push(state);
    }
    if (includeTourZipCode && postalCode) {
      locationParts.push(postalCode);
    }

    if (locationParts.length > 0) {
      lines.push(locationParts.join(", "));
    }

    return lines.filter((line, index, arr) => {
      if (!line) return false;
      if (index === 0) return true;
      return line.toLowerCase() !== arr[index - 1].toLowerCase();
    });
  };

  // Helper function to build Google Maps direction link
  const buildMapsDirectionLink = (property: TourProperty): string => {
    const lat = sanitizeText(String(property.latitude ?? ""));
    const lng = sanitizeText(String(property.longitude ?? ""));
    const hasLatLng = lat && lng;

    const address = [
      sanitizeText(property.address1),
      sanitizeText(property.city),
      sanitizeText(property.stateProvince),
      sanitizeText(property.postalCode),
    ]
      .filter(Boolean)
      .join(", ");

    const encodedAddress = encodeURIComponent(address);

    return hasLatLng
      ? `https://www.google.com/maps/dir//${encodedAddress}/@${lat},${lng}`
      : `https://www.google.com/maps/dir//${encodedAddress}`;
  };

  return (
    <div className="w-full h-full flex flex-col bg-light-gray">
      {/* Header */}
      <div
        style={{
          backgroundColor: "var(--Backgrounds-Background-3rd)",
          padding: "1.6rem 2.2rem",
        }}
      >
        <h1
          className="fancy"
          style={{ color: "var(--color-primary)", fontSize: "1.875rem" }}
        >
          {scheduleTitle}
        </h1>
      </div>
      {/* Body list */}
      <div className="flex-1 pb-24 flex flex-col">
        {isError && (
          <div className="p-8">
            <p className="text-red-600 text-sm">
              ⚠️ Error loading tour stops: {error?.toString() || "Unknown error"}
            </p>
          </div>
        )}
        
        {!hasTourContent ? (
          <p className="text-gray-500 mt-8 text-sm px-8">
            No properties available for the tour schedule.
          </p>
        ) : (
          <div className="flex-1 divide-y divide-gray-200 overflow-y-auto min-h-0 w-full">
            <ul className="min-h-0 w-full">
              {renderRows.map((row, rowIndex) => {
                if (row.kind === "stop") {
                  const stop = row.stop;
                  const stopLines = [stop.label, stop.address, stop.stopDetails]
                    .map((value) => sanitizeText(value))
                    .filter(Boolean);
                  const nestedDetails = stop.customStopDetails || [];
                  const nestedDetailsLine = nestedDetails
                    .map((detail) =>
                      sanitizeText(detail.name || detail.description || detail.notes || ""),
                    )
                    .filter(Boolean)
                    .join(" | ");
                  const stopDisplayLine = [stopLines.join(", "), nestedDetailsLine]
                    .filter(Boolean)
                    .join(" - ");

                  if (!stopDisplayLine) {
                    return null;
                  }

                  return (
                    <li
                      key={stop.stopId || stop.tourStopId || `stop-${rowIndex}`}
                      className="px-8 py-3 w-full"
                      style={{
                        backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#F5F7F7",
                      }}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mr-6 w-6 h-6 mt-0.5">
                          <EmeraldIcon
                            icon={isCurrentLocationStop(stop) ? "star" : "location_on"}
                            type={isCurrentLocationStop(stop) ? "filled" : "outlined"}
                            variant={isCurrentLocationStop(stop) ? "icon" : "symbol"}
                            color={isCurrentLocationStop(stop) ? "#F97316" : "#065F46"}
                            className="w-5 h-5"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 leading-relaxed truncate">
                            {stopDisplayLine}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                }

                // Render property
                const property = row.property;
                const propertyId = normalizePropertyId(property.id || property.propertyId);
                const stopDetails = property.stopDetails || "";

                const mapsHref = buildMapsDirectionLink(property);
                const displayLines = buildPropertyDisplayText(property);
                const fallbackLine =
                  sanitizeText(property.address1) ||
                  sanitizeText(property.name || property.propertyName) ||
                  "Property details unavailable";
                const displayLine =
                  displayLines.length > 0 ? displayLines.join(", ") : fallbackLine;
                const fullDisplayLine = sanitizeText(stopDetails)
                  ? `${displayLine} - ${sanitizeText(stopDetails)}`
                  : displayLine;
                
                // Get the icon color for the property
                const iconColor = getIconData(property).iconColor;
                const displayNumber = row.displayNumber;

                return (
                  <li
                    key={propertyId}
                    className="px-8 py-3 w-full"
                    style={{
                      backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#F5F7F7",
                    }}
                  >
                    {/* Main row: badge + address + directions */}
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-6 w-6 h-6 mt-0.5">
                        <div
                          style={{ "--dynamic-badge-color": iconColor } as Record<string, string>}
                        >
                          <EmeraldBadge
                            size={EmeraldBadgeSize.LARGE}
                            badgeCount={`${displayNumber || 0}`}
                            title={`Stop ${displayNumber || 0}`}
                            className="tour--badge"
                          />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm leading-snug font-medium text-gray-800 truncate">
                          {fullDisplayLine}
                        </p>
                      </div>

                      {showDirectionsArrow && mapsHref && (
                        <div className="flex-shrink-0 ml-4">
                          <a
                            href={mapsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${sanitizeText(property.address1) || "property"} in Google Maps`}
                          >
                            <EmeraldIcon
                              icon="directions"
                              type="outlined"
                              variant="symbol"
                              color="var(--color-primary)"
                              className="w-6 h-6"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveSurveyTourSchedule;
